/**
 * Tenant branding (white-label) for the web application: before sign-in it comes from the public
 * `GET /api/v1/branding` (resolved by host name), after sign-in from the session's tenant. Applies
 * the CSS variables, the document title and the favicon; components read the logo / name from it.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api.ts';
import { useSession } from './session.tsx';
import { DEFAULT_BRANDING, brandLogo, brandingStyleSheet, normalizeBranding, type Branding } from '../../core/branding/branding.ts';

interface BrandingContextValue { branding: Branding; logo: string | null; mark: string | null; /** tenant (organisation) name, when known */ organization: string | null; refresh: () => Promise<void> }
const Ctx = createContext<BrandingContextValue>({ branding: DEFAULT_BRANDING, logo: null, mark: null, organization: null, refresh: async () => undefined });

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const [publicBranding, setPublicBranding] = useState<(Branding & { organization?: string | null }) | null>(null);
  const load = async () => {
    try { const raw = await api.get<{ organization?: string | null }>('branding'); setPublicBranding({ ...normalizeBranding(raw).branding, organization: raw.organization ?? null }); } catch { setPublicBranding(DEFAULT_BRANDING); }
  };
  useEffect(() => { void load(); }, []);
  const branding = useMemo(() => session?.tenant?.branding ? normalizeBranding(session.tenant.branding).branding : publicBranding ?? DEFAULT_BRANDING, [session, publicBranding]);

  useEffect(() => {
    let style = document.getElementById('brand-vars') as HTMLStyleElement | null;
    if (!style) { style = document.createElement('style'); style.id = 'brand-vars'; document.head.appendChild(style); }
    style.textContent = brandingStyleSheet(branding);
    document.title = branding.appName;
    const icon = brandLogo(branding, 'mark');
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = icon ?? '/favicon.svg';
    link.type = icon ? icon.slice(5, icon.indexOf(';')) : 'image/svg+xml';
  }, [branding]);

  const organization = session?.tenant?.name ?? publicBranding?.organization ?? null;
  const value = useMemo<BrandingContextValue>(() => ({ branding, logo: brandLogo(branding, 'logo'), mark: brandLogo(branding, 'mark'), organization, refresh: load }), [branding, organization]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBranding(): BrandingContextValue { return useContext(Ctx); }

/** The tenant's logo, or the product mark when none is set. `plate` puts it on a white tile (dark backgrounds). */
export function BrandLogo({ kind = 'logo', height = 40, plate = false, className }: { kind?: 'logo' | 'mark'; height?: number; plate?: boolean; className?: string }) {
  const { logo, mark } = useBranding();
  const src = kind === 'mark' ? mark : logo;
  if (!src) return <img src="/logo.svg" alt="" className={className} width={height} height={height} style={{ height, width: height }} />;
  const img = <img src={src} alt="" className={className} style={{ height, maxWidth: kind === 'mark' ? height : height * 3.2, objectFit: 'contain', display: 'block' }} />;
  return plate ? <span className="brand-plate" style={{ padding: Math.max(4, Math.round(height / 6)) }}>{img}</span> : img;
}
